import { Flex } from '@chakra-ui/react';
import { Button, ButtonProps, Text } from '@everlywell/ui-kit';
import AnchorLink from 'components/AnchorLink';
import { Link } from 'gatsby';
import React, { MouseEventHandler } from 'react';
import { isFullUrl } from 'utils/helpers';

export type ProductPricingButtonProps = Pick<
  ButtonProps,
  | 'variant'
  | 'colorScheme'
  | 'rightIcon'
  | 'leftIcon'
  | 'size'
  | 'isDisabled'
  | 'isLoading'
> & {
  backgroundColorForTextArea?: string;
  startingText: string;
  priceText: string;
  billFrequencyText: string;
  buttonText: string;
  buttonPosition?: 'left' | 'right';
  buttonIconRight?: React.JSX.Element;
  buttonIconLeft?: React.JSX.Element;
  onButtonClick: MouseEventHandler<HTMLButtonElement>;
  linkTo: string;
  id?: string;
  hideSeparator?: boolean;
  buttonProps?: ButtonProps;
};

/**
 * [Add the description for what this component does here].
 */
function ProductPricingButton(props: ProductPricingButtonProps): JSX.Element {
  const {
    backgroundColorForTextArea = 'white',
    startingText,
    priceText,
    billFrequencyText,
    buttonText,
    variant = 'primary',
    colorScheme = 'greenCS',
    buttonPosition = 'right',
    onButtonClick,
    buttonIconRight,
    buttonIconLeft,
    size = 'md',
    isDisabled = false,
    isLoading = false,
    linkTo,
    id,
    hideSeparator = false,
    buttonProps = {},
  } = props;

  // Check if the linkTo is an anchor link (starts with '#') and it's not '#disclaimer'
  const isAnchorLink = linkTo.startsWith('#') && linkTo !== '#disclaimer';
  const isFullUrlRedirect = isFullUrl(linkTo);

  const buttonLinkProps: ButtonProps = {
    variant: variant,
    colorScheme: colorScheme,
    alignSelf: 'center',
    rightIcon: buttonIconRight,
    leftIcon: buttonIconLeft,
    onClick: onButtonClick,
    size: size,
    isDisabled: isDisabled,
    isLoading: isLoading,
    ...(isFullUrlRedirect
      ? { href: linkTo, as: 'a' }
      : { to: linkTo, as: Link }),
    ...buttonProps,
  };

  return (
    <Flex
      id={id}
      flexDirection={`${buttonPosition === 'left' ? 'row-reverse' : 'row'}`}
      justifyContent="space-between"
      backgroundColor={backgroundColorForTextArea}
      padding={2}
      borderRadius={50}
    >
      <Text
        size="lg"
        alignSelf="center"
        marginLeft={[3, null, 6]}
        marginRight={[3, null, 6]}
      >
        {startingText}{' '}
        {priceText && (
          <>
            <Text as="span" fontSize="lg">
              $
            </Text>
            <Text as="span" fontSize="2xl">
              {priceText}
            </Text>
          </>
        )}
        {hideSeparator ? ' ' : '/'}
        <Text as="span" fontSize={['sm', null, 'lg']} color="tints.darkGrey">
          {billFrequencyText}
        </Text>
      </Text>

      {isAnchorLink ? (
        <AnchorLink href={linkTo}>
          <Button
            variant={variant}
            colorScheme={colorScheme}
            alignSelf="center"
            rightIcon={buttonIconRight}
            leftIcon={buttonIconLeft}
            onClick={onButtonClick}
            size={size}
            isDisabled={isDisabled}
            isLoading={isLoading}
          >
            {buttonText}
          </Button>
        </AnchorLink>
      ) : (
        <Button {...buttonLinkProps} onClick={onButtonClick}>
          {buttonText}
        </Button>
      )}
    </Flex>
  );
}

export default ProductPricingButton;
